import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Box } from '@nerdwallet/currency';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import { useMediaQuery } from '~/app/lib/use-media-query';

import get from 'lodash/get';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDispatch } from 'react-redux';
import { COMPONENT_UPDATED } from '~/app/lib/redux/actions/action-types';
import {
  boolCheckboxSpec,
  stringInputSpec,
  stringSelectSpec,
  group,
} from '../../lib/editing-configs';

import BackgroundColor from '../background-color/background-color';
import UniversalButton from '../universal-button/universal-button';

import './border-banner.scss';
import BorderBannerBackgroundWrapper from './border-banner-background-wrapper';
import UniversalText from '../universal-text/universal-text';
import UniversalContainer from '../universal-container';

export const updateComponent = (component) => ({
  type: COMPONENT_UPDATED,
  component,
});

const defaultProps = {
  disableDefaultProps: false,
  preheader: {
    text: 'our mission',
    groupButton: {
      center: true,
    },
    textType: 'accent',
    textColor: 'white',
    textSize: 'small',
  },
  title: {
    text: 'To provide clarity for all of life’s financial decisions.\n\n',
    groupButton: {
      center: true,
    },
    textType: 'custom',
    fontFamily: 'Chronicle',
    customTextSize: 27,
    textColor: 'white',
  },
  subtitle: {
    text: 'Make all the right money moves',
    groupButton: {
      center: true,
    },
    textColor: 'white',
    textType: 'custom',
    customTextSize: 18,
  },
  universalButton: {
    buttonText: 'Sign in or create account',
    ctaExtraText: {
      text: '',
    },
  },
};

const BorderBanner = ({
  disableDefaultProps,
  container,
  preheader,
  title,
  subtitle,
  untouched,
  componentId,
  isCopy,
  bgOptions,
  borderBgOptions,
  roundedCorners,
  // For UniversalButton
  universalButton,
}) => {
  const size = useMediaQuery();
  const dispatch = useDispatch();
  const { flatOutline, selected } = useBuilder();
  const selectedComponent = flatOutline?.[selected];
  const borderBannerWithEmptyPropsRef = useRef();

  const shouldShowActionButton =
    universalButton?.buttonType && universalButton?.buttonType !== 'none';

  const fromGrid = selectedComponent?.type === 'Grid';

  // saves the very initial version of the UniversalborderBanner
  // to be used when "disableDefaultProps" is activated
  useEffect(() => {
    if (untouched && !borderBannerWithEmptyPropsRef.current) {
      borderBannerWithEmptyPropsRef.current = selectedComponent;
    }
  }, [selectedComponent, untouched]);

  useEffect(() => {
    if (
      untouched &&
      selectedComponent?.id === componentId &&
      !disableDefaultProps &&
      !fromGrid &&
      !isCopy
    ) {
      dispatch(
        updateComponent({
          ...selectedComponent,
          props: { ...defaultProps, untouched: false },
        })
      );
    }
  }, [
    componentId,
    disableDefaultProps,
    selectedComponent,
    untouched,
    fromGrid,
    isCopy,
    dispatch,
  ]);

  useEffect(() => {
    if (disableDefaultProps) {
      dispatch(
        updateComponent({
          ...borderBannerWithEmptyPropsRef.current,
          props: {
            ...borderBannerWithEmptyPropsRef.current?.props,
            disableDefaultProps: true,
          },
        })
      );
    }
  }, [disableDefaultProps, dispatch, untouched]);

  return (
    <UniversalContainer {...container}>
      <Box className={classes('text-align-center')}>
        <BorderBannerBackgroundWrapper
          bgOptions={borderBgOptions}
          size={size}
          className={classNames('bg-border', {
            'rounded-corners': roundedCorners,
          })}
        >
          <BorderBannerBackgroundWrapper
            bgOptions={bgOptions}
            size={size}
            className={classNames('border-banner', {
              'rounded-corners': roundedCorners,
            })}
          >
            <Box
              className={classNames([
                'border-banner__content',
                'centered-in-parent',
                { [classes('padding-bottom-4')]: !shouldShowActionButton },
              ])}
              I
            >
              <Box className="border-banner__preheader">
                {preheader && <UniversalText {...preheader} />}
              </Box>
              {title && <UniversalText {...title} />}
              <Box>{subtitle && <UniversalText {...subtitle} />}</Box>
              {true && (
                <Box
                  className={classNames([
                    classes('padding-top-4'),
                    classes('padding-bottom-4'),
                  ])}
                >
                  <UniversalButton
                    buttonClass="border-banner__button"
                    {...universalButton}
                    alignment="center"
                  />
                </Box>
              )}
            </Box>
          </BorderBannerBackgroundWrapper>
        </BorderBannerBackgroundWrapper>
      </Box>
    </UniversalContainer>
  );
};

BorderBanner.displayName = 'BorderBanner';

BorderBanner.propTypes = {
  title: UniversalText.propTypes,
  subtitle: UniversalText.propTypes,
  roundedCorners: PropTypes.bool,
  ...UniversalButton.propTypes,
  ...UniversalContainer.propTypes,
  backgroundImage: PropTypes.bool,
  backgroundSize: PropTypes.oneOf(['contain', 'cover', 'auto']),
  desktopAlignment: PropTypes.oneOf(['left', 'center', 'right']),
  mobileAlignment: PropTypes.oneOf(['top', 'center', 'bottom']),
  backgroundImageSrcDesktop: PropTypes.string,
  backgroundImageSrcTablet: PropTypes.string,
  backgroundImageSrcMobile: PropTypes.string,
};

BorderBanner.defaultProps = {
  buttonType: 'custom',
  roundedCorners: false,
  bgOptions: {
    hexColor: '',
    backgroundColor: 'green-dark',
    backgroundSize: 'cover',
  },
  borderBgOptions: {
    hexColor: '',
    backgroundColor: 'green-light',
    backgroundSize: 'cover',
  },
  ...UniversalButton.defaultProps,
};

const borderBannerImageTypeInput = (baseName, fieldName, label, helpText) => [
  boolCheckboxSpec(`${baseName}.${fieldName}`, label, {
    help: helpText,
  }),
  stringSelectSpec(
    `${baseName}.backgroundSize`,
    'Background Size',
    [
      { label: 'Cover', value: 'cover' },
      { label: 'Contain', value: 'contain' },
      { label: 'Auto', value: 'auto' },
    ],
    {
      help: 'Only applies to Desktop and Tablet',
    }
  ),
  stringSelectSpec(
    `${baseName}.desktopAlignment`,
    'Desktop Alignment',
    [
      { label: 'Left', value: 'left' },
      { label: 'Center', value: 'center' },
      { label: 'Right', value: 'right' },
    ],
    {
      help: 'Only applies to Desktop and Tablet',
    }
  ),
  stringSelectSpec(
    `${baseName}.mobileAlignment`,
    'Mobile Alignment',
    [
      { label: 'Top', value: 'top' },
      { label: 'Middle', value: 'center' },
      { label: 'Bottom', value: 'bottom' },
    ],
    {
      help: 'Only applies to Mobile',
    }
  ),
  stringInputSpec(
    `${baseName}.backgroundImageSrcDesktop`,
    'Background Image URL (Desktop)'
  ),
  stringInputSpec(
    `${baseName}.backgroundImageSrcTablet`,
    'Background Image URL (Tablet)'
  ),
  stringInputSpec(
    `${baseName}.backgroundImageSrcMobile`,
    'Background Image URL (Mobile)'
  ),
];

BorderBanner.getEditingConfig = (componentDocument, fieldName = '') => {
  const getInputName = (name) => {
    if (fieldName) {
      return `${fieldName}.${name}`;
    }
    return name;
  };

  const borderBannerForeGroundImageTypeInputConfig =
    get(componentDocument, 'props.bgOptions.backgroundImage', false) === true
      ? [
          ...borderBannerImageTypeInput(
            'bgOptions',
            'backgroundImage',
            'Enable foreground Image?',
            'If checked enables foreground image output.'
          ),
        ]
      : [
          borderBannerImageTypeInput(
            'bgOptions',
            'backgroundImage',
            'Enable foreground Image?',
            'If checked enables foreground image output.'
          )[0],
        ];

  const borderBannerBackgroundImageTypeInputConfig =
    get(componentDocument, 'props.borderBgOptions.backgroundImage', false) ===
    true
      ? [
          ...borderBannerImageTypeInput(
            'borderBgOptions',
            'backgroundImage',
            'Enable background Image?',
            'If checked enables background image output.'
          ),
        ]
      : [
          borderBannerImageTypeInput(
            'borderBgOptions',
            'backgroundImage',
            'Enable background Image?',
            'If checked enables background image output.'
          )[0],
        ];

  return [
    boolCheckboxSpec(`disableDefaultProps`, 'Disable Default Properties?'),
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      [
        'backgroundColor',
        'backgroundImage',
        'border',
        'margin',
        'padding',
        'hide',
        'jumpLinkId',
      ],
      `${getInputName('container')}`
    ),
    group('Banner Styles', [
      ...BackgroundColor.getEditingConfig(
        componentDocument,
        'borderBgOptions',
        'Background Color'
      ),
      ...BackgroundColor.getEditingConfig(
        componentDocument,
        'bgOptions',
        'Foreground Color'
      ),
      ...borderBannerBackgroundImageTypeInputConfig,
      ...borderBannerForeGroundImageTypeInputConfig,
      boolCheckboxSpec('roundedCorners', 'Rounded Corners?'),
    ]),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Pre-header',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      `${getInputName('preheader')}`,
      { text: { textType: 'text' } }
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      `${getInputName('title')}`,
      { text: { textType: 'text' } }
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Subtitle',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      `${getInputName('subtitle')}`,
      { text: { textType: 'text' } }
    ),
    ...UniversalButton.getEditingConfig(
      componentDocument,
      'universalButton',
      'CTA Button'
    ),
  ];
};

export default BorderBanner;
